/** AI 水印的唯一几何计算入口，供 CDN、Canvas 和 DOM 适配层共同使用 */
import type { AIWatermarkAsset, AIWatermarkLayoutOptions, AIWatermarkRect, AIWatermarkSize } from './types';

/** 新规范中整块可见水印高度占图片短边的比例，目标高度向上取整到像素 */
export const AI_WATERMARK_HEIGHT_RATIO = 0.05;
/** 新规范中可见水印距左侧、下方的短边比例 */
export const AI_WATERMARK_MARGIN_RATIO = 0.02;

function assertSize(size: AIWatermarkSize) {
    if (!Number.isFinite(size.width) || !Number.isFinite(size.height) || size.width <= 0 || size.height <= 0) {
        throw new Error('水印计算需要有效的图片和素材尺寸');
    }
}

/**
 * 按整块可见水印高计算矩形，默认将高度向上取整到像素，再等比计算宽度
 * @param image - 当前渲染或最终导出的图片尺寸
 * @param asset - 经核对的水印素材元数据
 * @param options - 默认使用新规范，旧参数仅由兼容入口传入
 * @returns 可见水印相对于图片左上角的矩形
 */
export function getAIWatermarkLayout(
    image: AIWatermarkSize,
    asset: AIWatermarkAsset,
    options: AIWatermarkLayoutOptions = {},
): AIWatermarkRect {
    assertSize(image);
    assertSize(asset);
    const source = asset.contentRect;
    assertSize(source);
    if (
        !Number.isFinite(source.x) ||
        !Number.isFinite(source.y) ||
        source.x < 0 ||
        source.y < 0 ||
        source.x + source.width > asset.width ||
        source.y + source.height > asset.height
    ) {
        throw new Error('水印可见区域超出素材边界');
    }
    const {
        position = 'SouthWest',
        heightRatio = AI_WATERMARK_HEIGHT_RATIO,
        marginRatio = AI_WATERMARK_MARGIN_RATIO,
        marginReference = 'image-short-side',
        roundHeightUp = true,
    } = options;
    if (!Number.isFinite(heightRatio) || heightRatio <= 0 || !Number.isFinite(marginRatio) || marginRatio < 0) {
        throw new Error('水印比例必须为有效的非负数，且高度比例不能为零');
    }
    const shortSide = Math.min(image.width, image.height);
    const targetHeight = shortSide * heightRatio;
    const height = roundHeightUp ? Math.ceil(targetHeight) : targetHeight;
    const width = (height * source.width) / source.height;
    const margin = (marginReference === 'watermark-width' ? width : shortSide) * marginRatio;
    return {
        x: position.includes('West') ? margin : image.width - width - margin,
        y: position.includes('North') ? margin : image.height - height - margin,
        width,
        height,
    };
}

/**
 * 计算居中 contain 图片的实际显示区域，预览留白不计入图片尺寸
 * @param image - 图片固有尺寸
 * @param container - 容器显示尺寸
 * @returns 图片相对容器的可见区域
 */
export function getAIWatermarkImageRect(image: AIWatermarkSize, container: AIWatermarkSize): AIWatermarkRect {
    assertSize(image);
    assertSize(container);
    const scale = Math.min(container.width / image.width, container.height / image.height);
    const width = image.width * scale;
    const height = image.height * scale;
    return { x: (container.width - width) / 2, y: (container.height - height) / 2, width, height };
}

/**
 * 返回 DOM 水印裁剪容器及内部图片样式，正确去除素材透明留白
 * @param imageRect - 图片在预览容器内的实际显示区域
 * @param asset - 水印素材元数据
 * @returns 外层裁剪容器和内层 img 的样式；数值单位为 CSS 像素
 */
export function getAIWatermarkOverlayStyle(imageRect: AIWatermarkRect, asset: AIWatermarkAsset) {
    const layout = getAIWatermarkLayout(imageRect, asset);
    const scale = layout.height / asset.contentRect.height;
    return {
        container: {
            position: 'absolute' as const,
            overflow: 'hidden' as const,
            left: imageRect.x + layout.x,
            top: imageRect.y + layout.y,
            width: layout.width,
            height: layout.height,
            opacity: asset.opacity ?? 0.8,
        },
        image: {
            position: 'absolute' as const,
            maxWidth: 'none',
            width: asset.width * scale,
            height: asset.height * scale,
            left: -asset.contentRect.x * scale,
            top: -asset.contentRect.y * scale,
        },
    };
}
